/*
 * GroupTravel Messages
 *
 * This contains all the text for the GroupTravel (Voyager en groupe) container.
 */

import { defineMessages } from 'react-intl';

export const groupTravelScope = 'app.containers.GroupTravel';

export const groupTravelMessages = {
  metaTitle: {
    id: `${groupTravelScope}.metaTitle`,
    defaultMessage: 'Voyager en groupe en train ou en bus | Devis sous 24h',
    en: 'Group travel by train or bus | Quote within 24 hours',
    it: 'Viaggi di gruppo in treno o autobus | Preventivo entro 24 ore',
    es: 'Viajes en grupo en tren o autobús | Presupuesto en 24 horas',
    de: 'Gruppenreisen mit Zug oder Bus | Angebot innerhalb von 24 Stunden',
    nl: 'Groepsreizen per trein of bus | Offerte binnen 24 uur',
  },
  metaDescription: {
    id: `${groupTravelScope}.metaDescription`,
    defaultMessage:
      'Réservez vos voyages en groupe en train ou en bus avec Tictactrip. De 10 à plusieurs centaines de personnes, à des prix très compétitifs. Devis personnalisé sous 24 heures.',
    en: 'Book your group trips by train or bus with Tictactrip. From 10 to several hundred people, at highly competitive prices. Personalized quote within 24 hours.',
    it: 'Prenota i tuoi viaggi di gruppo in treno o autobus con Tictactrip. Da 10 a diverse centinaia di persone, a prezzi molto competitivi. Preventivo personalizzato entro 24 ore.',
    es: 'Reserva tus viajes en grupo en tren o autobús con Tictactrip. De 10 a varios cientos de personas, a precios muy competitivos. Presupuesto personalizado en 24 horas.',
    de: 'Buchen Sie Ihre Gruppenreisen mit Zug oder Bus bei Tictactrip. Von 10 bis zu mehreren Hundert Personen zu sehr wettbewerbsfähigen Preisen. Personalisiertes Angebot innerhalb von 24 Stunden.',
    nl: 'Boek uw groepsreizen per trein of bus met Tictactrip. Van 10 tot enkele honderden personen, tegen zeer concurrerende prijzen. Persoonlijke offerte binnen 24 uur.',
  },
  metaKeywords: {
    id: `${groupTravelScope}.metaKeywords`,
    defaultMessage:
      'voyage en groupe, train de groupe, bus de groupe, devis groupe, réservation groupe, billet train groupe, billet bus groupe, voyage scolaire, voyage entreprise, voyage association',
    en: 'group travel, group train, group bus, group quote, group booking, group train ticket, group bus ticket, school trip, corporate travel, association travel',
    it: 'viaggio di gruppo, treno di gruppo, autobus di gruppo, preventivo gruppo, prenotazione gruppo, biglietto treno gruppo, biglietto autobus gruppo, viaggio scolastico, viaggio aziendale',
    es: 'viaje en grupo, tren de grupo, autobús de grupo, presupuesto grupo, reserva grupo, billete tren grupo, billete autobús grupo, viaje escolar, viaje de empresa',
    de: 'Gruppenreise, Gruppenzug, Gruppenbus, Gruppenangebot, Gruppenbuchung, Gruppenzugticket, Gruppenbusticket, Schulreise, Geschäftsreise',
    nl: 'groepsreis, groepstrein, groepsbus, groepsofferte, groepsboeking, groepstreinticket, groepsbusticket, schoolreis, zakenreis',
  },

  // Header / Hero
  heroTitle: {
    id: `${groupTravelScope}.heroTitle`,
    defaultMessage: 'Voyager en groupe',
    en: 'Group travel',
    it: 'Viaggiare in gruppo',
    es: 'Viajar en grupo',
    de: 'In der Gruppe reisen',
    nl: 'Reizen in groep',
  },
  heroTitleHighlight: {
    id: `${groupTravelScope}.heroTitleHighlight`,
    defaultMessage: ' en train ou en bus.',
    en: ' by train or bus.',
    it: ' in treno o in autobus.',
    es: ' en tren o autobús.',
    de: ' mit Zug oder Bus.',
    nl: ' per trein of bus.',
  },
  heroSubtitle: {
    id: `${groupTravelScope}.heroSubtitle`,
    defaultMessage:
      'De 10 à plusieurs centaines de voyageurs. Des prix très compétitifs négociés avec nos transporteurs partenaires. Une équipe dédiée pour organiser votre trajet de A à Z.',
    en: 'From 10 to several hundred travellers. Highly competitive prices negotiated with our partner carriers. A dedicated team to organise your trip from A to Z.',
    it: 'Da 10 a diverse centinaia di viaggiatori. Prezzi molto competitivi negoziati con i nostri vettori partner. Un team dedicato per organizzare il tuo viaggio dalla A alla Z.',
    es: 'De 10 a varios cientos de viajeros. Precios muy competitivos negociados con nuestros transportistas asociados. Un equipo dedicado para organizar su viaje de la A a la Z.',
    de: 'Von 10 bis zu mehreren Hundert Reisenden. Sehr wettbewerbsfähige Preise, ausgehandelt mit unseren Partner-Transportunternehmen. Ein engagiertes Team, das Ihre Reise von A bis Z organisiert.',
    nl: 'Van 10 tot enkele honderden reizigers. Zeer concurrerende prijzen, onderhandeld met onze partnervervoerders. Een toegewijd team om uw reis van A tot Z te organiseren.',
  },
  heroCta: {
    id: `${groupTravelScope}.heroCta`,
    defaultMessage: 'Demander un devis gratuit',
    en: 'Request a free quote',
    it: 'Richiedi un preventivo gratuito',
    es: 'Solicitar un presupuesto gratuito',
    de: 'Kostenloses Angebot anfordern',
    nl: 'Vraag een gratis offerte aan',
  },
  responseTime: {
    id: `${groupTravelScope}.responseTime`,
    defaultMessage: 'Réponse sous 24 heures.',
    en: 'Response within 24 hours.',
    it: 'Risposta entro 24 ore.',
    es: 'Respuesta en 24 horas.',
    de: 'Antwort innerhalb von 24 Stunden.',
    nl: 'Antwoord binnen 24 uur.',
  },

  // Why us section
  whyUsTitle: {
    id: `${groupTravelScope}.whyUsTitle`,
    defaultMessage:
      'Pourquoi réserver votre voyage de groupe avec Tictactrip ?',
    en: 'Why book your group trip with Tictactrip?',
    it: 'Perché prenotare il tuo viaggio di gruppo con Tictactrip?',
    es: '¿Por qué reservar tu viaje en grupo con Tictactrip?',
    de: 'Warum Ihre Gruppenreise mit Tictactrip buchen?',
    nl: 'Waarom uw groepsreis boeken bij Tictactrip?',
  },
  benefitPriceTitle: {
    id: `${groupTravelScope}.benefitPriceTitle`,
    defaultMessage: 'Des prix négociés',
    en: 'Negotiated prices',
    it: 'Prezzi negoziati',
    es: 'Precios negociados',
    de: 'Verhandelte Preise',
    nl: 'Onderhandelde prijzen',
  },
  benefitPriceDescription: {
    id: `${groupTravelScope}.benefitPriceDescription`,
    defaultMessage:
      'Grâce à nos partenariats avec la SNCF, Flixbus, Blablacar, Ouigo et de nombreux autres transporteurs, nous obtenons des tarifs préférentiels pour les groupes, jusqu’à -50 % par rapport au prix public.',
    en: 'Thanks to our partnerships with SNCF, Flixbus, Blablacar, Ouigo and many other carriers, we secure preferential group rates, up to -50% off the public price.',
    it: 'Grazie alle nostre partnership con SNCF, Flixbus, Blablacar, Ouigo e molti altri vettori, otteniamo tariffe preferenziali per i gruppi, fino al -50% rispetto al prezzo al pubblico.',
    es: 'Gracias a nuestras alianzas con SNCF, Flixbus, Blablacar, Ouigo y muchos otros transportistas, conseguimos tarifas preferenciales para grupos, hasta un -50% sobre el precio público.',
    de: 'Dank unserer Partnerschaften mit SNCF, Flixbus, Blablacar, Ouigo und vielen anderen Transportunternehmen erhalten wir Vorzugspreise für Gruppen, bis zu -50 % gegenüber dem öffentlichen Preis.',
    nl: 'Dankzij onze partnerschappen met SNCF, Flixbus, Blablacar, Ouigo en vele andere vervoerders krijgen wij voorkeurstarieven voor groepen, tot -50% korting op de publieksprijs.',
  },
  benefitTeamTitle: {
    id: `${groupTravelScope}.benefitTeamTitle`,
    defaultMessage: 'Une équipe dédiée',
    en: 'A dedicated team',
    it: 'Un team dedicato',
    es: 'Un equipo dedicado',
    de: 'Ein engagiertes Team',
    nl: 'Een toegewijd team',
  },
  benefitTeamDescription: {
    id: `${groupTravelScope}.benefitTeamDescription`,
    defaultMessage:
      'Un interlocuteur unique vous accompagne de la demande de devis jusqu’à l’arrivée à destination : choix du transporteur, modifications, gestion des imprévus.',
    en: 'A single point of contact supports you from the quote request to arrival at destination: choice of carrier, changes, handling unexpected events.',
    it: 'Un unico interlocutore ti accompagna dalla richiesta di preventivo all’arrivo a destinazione: scelta del vettore, modifiche, gestione degli imprevisti.',
    es: 'Un interlocutor único te acompaña desde la solicitud de presupuesto hasta la llegada a destino: elección del transportista, modificaciones, gestión de imprevistos.',
    de: 'Ein einziger Ansprechpartner begleitet Sie von der Angebotsanfrage bis zur Ankunft am Ziel: Wahl des Transportunternehmens, Änderungen, Umgang mit unerwarteten Ereignissen.',
    nl: 'Eén aanspreekpunt begeleidt u van de offerteaanvraag tot de aankomst op bestemming: keuze van vervoerder, wijzigingen, beheer van onverwachte gebeurtenissen.',
  },
  benefitFlexibilityTitle: {
    id: `${groupTravelScope}.benefitFlexibilityTitle`,
    defaultMessage: 'Une flexibilité totale',
    en: 'Full flexibility',
    it: 'Totale flessibilità',
    es: 'Flexibilidad total',
    de: 'Vollständige Flexibilität',
    nl: 'Volledige flexibiliteit',
  },
  benefitFlexibilityDescription: {
    id: `${groupTravelScope}.benefitFlexibilityDescription`,
    defaultMessage:
      'Modification du nombre de passagers jusqu’à 48 h avant le départ, options de paiement adaptées (mandat administratif, virement, carte) et facturation centralisée.',
    en: 'Change the number of passengers up to 48 hours before departure, suitable payment options (administrative order, bank transfer, card) and centralised invoicing.',
    it: 'Modifica del numero di passeggeri fino a 48 ore prima della partenza, opzioni di pagamento adatte (mandato amministrativo, bonifico, carta) e fatturazione centralizzata.',
    es: 'Modificación del número de pasajeros hasta 48 h antes de la salida, opciones de pago adaptadas (mandato administrativo, transferencia, tarjeta) y facturación centralizada.',
    de: 'Änderung der Passagieranzahl bis zu 48 Stunden vor Abfahrt, passende Zahlungsoptionen (Verwaltungsanordnung, Überweisung, Karte) und zentralisierte Rechnungsstellung.',
    nl: 'Wijziging van het aantal passagiers tot 48 uur voor vertrek, geschikte betalingsopties (administratieve opdracht, overschrijving, kaart) en gecentraliseerde facturering.',
  },
  benefitEcoTitle: {
    id: `${groupTravelScope}.benefitEcoTitle`,
    defaultMessage: 'Un transport bas-carbone',
    en: 'Low-carbon transport',
    it: 'Trasporto a basse emissioni di carbonio',
    es: 'Transporte bajo en carbono',
    de: 'CO₂-armer Transport',
    nl: 'Koolstofarm vervoer',
  },
  benefitEcoDescription: {
    id: `${groupTravelScope}.benefitEcoDescription`,
    defaultMessage:
      'Le train émet 30 à 50 fois moins de CO₂ que l’avion. Voyager en groupe, c’est aussi diviser l’empreinte carbone par personne et faire un choix responsable.',
    en: 'Trains emit 30 to 50 times less CO₂ than planes. Travelling as a group also means dividing the carbon footprint per person and making a responsible choice.',
    it: 'Il treno emette dalle 30 alle 50 volte meno CO₂ dell’aereo. Viaggiare in gruppo significa anche dividere l’impronta di carbonio per persona e fare una scelta responsabile.',
    es: 'El tren emite 30 a 50 veces menos CO₂ que el avión. Viajar en grupo también significa dividir la huella de carbono por persona y tomar una decisión responsable.',
    de: 'Der Zug stößt 30 bis 50 Mal weniger CO₂ aus als das Flugzeug. In der Gruppe zu reisen, bedeutet auch, den CO₂-Fußabdruck pro Person zu teilen und eine verantwortungsvolle Wahl zu treffen.',
    nl: 'Treinen stoten 30 tot 50 keer minder CO₂ uit dan vliegtuigen. Reizen in groep betekent ook de koolstofvoetafdruk per persoon delen en een verantwoorde keuze maken.',
  },

  // Group sizes
  groupSizesTitle: {
    id: `${groupTravelScope}.groupSizesTitle`,
    defaultMessage: 'Tous les types de groupes',
    en: 'All types of groups',
    it: 'Tutti i tipi di gruppi',
    es: 'Todos los tipos de grupos',
    de: 'Alle Arten von Gruppen',
    nl: 'Alle soorten groepen',
  },
  groupSizesSubtitle: {
    id: `${groupTravelScope}.groupSizesSubtitle`,
    defaultMessage:
      'Que vous soyez 10 ou 500, nous trouvons la solution la mieux adaptée à votre projet.',
    en: 'Whether you are 10 or 500, we find the solution that best fits your project.',
    it: 'Che siate 10 o 500, troviamo la soluzione più adatta al vostro progetto.',
    es: 'Tanto si sois 10 como 500, encontramos la solución que mejor se adapta a vuestro proyecto.',
    de: 'Egal, ob Sie 10 oder 500 sind – wir finden die für Ihr Projekt am besten geeignete Lösung.',
    nl: 'Of u nu met 10 of 500 bent, wij vinden de oplossing die het beste bij uw project past.',
  },
  groupSchoolTitle: {
    id: `${groupTravelScope}.groupSchoolTitle`,
    defaultMessage: 'Voyages scolaires',
    en: 'School trips',
    it: 'Viaggi scolastici',
    es: 'Viajes escolares',
    de: 'Schulreisen',
    nl: 'Schoolreizen',
  },
  groupSchoolDescription: {
    id: `${groupTravelScope}.groupSchoolDescription`,
    defaultMessage:
      'Sorties scolaires, classes de découverte, voyages de fin d’année. Tarifs jeunes, accompagnateurs gratuits, paiement par mandat administratif.',
    en: 'School outings, discovery classes, end-of-year trips. Youth fares, free chaperones, payment by administrative order.',
    it: 'Gite scolastiche, classi di scoperta, viaggi di fine anno. Tariffe giovani, accompagnatori gratuiti, pagamento con mandato amministrativo.',
    es: 'Salidas escolares, clases de descubrimiento, viajes de fin de año. Tarifas para jóvenes, acompañantes gratuitos, pago por mandato administrativo.',
    de: 'Schulausflüge, Entdeckungsklassen, Abschlussreisen. Jugendtarife, kostenlose Begleitpersonen, Zahlung per Verwaltungsanordnung.',
    nl: 'Schooluitstapjes, ontdekkingsklassen, eindejaarsreizen. Jeugdtarieven, gratis begeleiders, betaling per administratieve opdracht.',
  },
  groupBusinessTitle: {
    id: `${groupTravelScope}.groupBusinessTitle`,
    defaultMessage: 'Voyages d’entreprise',
    en: 'Corporate travel',
    it: 'Viaggi aziendali',
    es: 'Viajes de empresa',
    de: 'Geschäftsreisen',
    nl: 'Zakenreizen',
  },
  groupBusinessDescription: {
    id: `${groupTravelScope}.groupBusinessDescription`,
    defaultMessage:
      'Séminaires, conventions, team building, salons professionnels. Facturation centralisée, justificatifs comptables et conditions adaptées aux entreprises.',
    en: 'Seminars, conventions, team building, trade fairs. Centralised invoicing, accounting documents and business-friendly terms.',
    it: 'Seminari, convegni, team building, fiere professionali. Fatturazione centralizzata, documenti contabili e condizioni adatte alle aziende.',
    es: 'Seminarios, convenciones, team building, ferias profesionales. Facturación centralizada, justificantes contables y condiciones adaptadas a las empresas.',
    de: 'Seminare, Tagungen, Teambuilding, Fachmessen. Zentralisierte Rechnungsstellung, buchhalterische Belege und unternehmensfreundliche Bedingungen.',
    nl: 'Seminars, congressen, teambuilding, vakbeurzen. Gecentraliseerde facturering, boekhoudkundige bewijsstukken en zakelijke voorwaarden.',
  },
  groupAssociationTitle: {
    id: `${groupTravelScope}.groupAssociationTitle`,
    defaultMessage: 'Associations & clubs',
    en: 'Associations & clubs',
    it: 'Associazioni e club',
    es: 'Asociaciones y clubes',
    de: 'Vereine & Clubs',
    nl: 'Verenigingen & clubs',
  },
  groupAssociationDescription: {
    id: `${groupTravelScope}.groupAssociationDescription`,
    defaultMessage:
      'Clubs sportifs, associations culturelles, voyages organisés. Des tarifs préférentiels pour les clubs et associations qui voyagent régulièrement.',
    en: 'Sports clubs, cultural associations, organised trips. Preferential rates for clubs and associations that travel regularly.',
    it: 'Club sportivi, associazioni culturali, viaggi organizzati. Tariffe preferenziali per club e associazioni che viaggiano regolarmente.',
    es: 'Clubes deportivos, asociaciones culturales, viajes organizados. Tarifas preferenciales para clubes y asociaciones que viajan regularmente.',
    de: 'Sportvereine, Kulturvereine, organisierte Reisen. Vorzugspreise für Vereine und Verbände, die regelmäßig reisen.',
    nl: 'Sportclubs, culturele verenigingen, georganiseerde reizen. Voorkeurstarieven voor clubs en verenigingen die regelmatig reizen.',
  },
  groupEventTitle: {
    id: `${groupTravelScope}.groupEventTitle`,
    defaultMessage: 'Mariages & événements',
    en: 'Weddings & events',
    it: 'Matrimoni ed eventi',
    es: 'Bodas y eventos',
    de: 'Hochzeiten & Events',
    nl: 'Bruiloften & evenementen',
  },
  groupEventDescription: {
    id: `${groupTravelScope}.groupEventDescription`,
    defaultMessage:
      'Mariages, EVJF/EVG, festivals, concerts, événements sportifs. Une logistique simple pour acheminer vos invités au même endroit, au même moment.',
    en: 'Weddings, hen/stag parties, festivals, concerts, sporting events. Simple logistics to bring your guests to the same place at the same time.',
    it: 'Matrimoni, addii al celibato/nubilato, festival, concerti, eventi sportivi. Una logistica semplice per portare i tuoi ospiti nello stesso luogo nello stesso momento.',
    es: 'Bodas, despedidas, festivales, conciertos, eventos deportivos. Una logística sencilla para llevar a vuestros invitados al mismo lugar, al mismo tiempo.',
    de: 'Hochzeiten, Junggesellenabschiede, Festivals, Konzerte, Sportveranstaltungen. Eine einfache Logistik, um Ihre Gäste zur selben Zeit an denselben Ort zu bringen.',
    nl: 'Bruiloften, vrijgezellenfeesten, festivals, concerten, sportevenementen. Eenvoudige logistiek om uw gasten op dezelfde plek en hetzelfde moment te brengen.',
  },

  // How it works
  howItWorksTitle: {
    id: `${groupTravelScope}.howItWorksTitle`,
    defaultMessage: 'Comment ça marche ?',
    en: 'How does it work?',
    it: 'Come funziona?',
    es: '¿Cómo funciona?',
    de: 'Wie funktioniert das?',
    nl: 'Hoe werkt het?',
  },
  step1Title: {
    id: `${groupTravelScope}.step1Title`,
    defaultMessage: '1. Demandez votre devis',
    en: '1. Request your quote',
    it: '1. Richiedi il tuo preventivo',
    es: '1. Solicita tu presupuesto',
    de: '1. Fordern Sie Ihr Angebot an',
    nl: '1. Vraag uw offerte aan',
  },
  step1Description: {
    id: `${groupTravelScope}.step1Description`,
    defaultMessage:
      'Écrivez-nous à {email} avec votre trajet, vos dates et le nombre de passagers. C’est gratuit et sans engagement.',
    en: 'Email us at {email} with your route, dates and number of passengers. Free and with no commitment.',
    it: 'Scrivici a {email} con il tuo itinerario, le date e il numero di passeggeri. È gratuito e senza impegno.',
    es: 'Escríbenos a {email} con tu trayecto, tus fechas y el número de pasajeros. Es gratis y sin compromiso.',
    de: 'Schreiben Sie uns an {email} mit Ihrer Strecke, Ihren Daten und der Anzahl der Passagiere. Kostenlos und unverbindlich.',
    nl: 'Mail ons op {email} met uw traject, data en aantal passagiers. Gratis en zonder verplichting.',
  },
  step2Title: {
    id: `${groupTravelScope}.step2Title`,
    defaultMessage: '2. Recevez une proposition sous 24 h',
    en: '2. Receive a proposal within 24 hours',
    it: '2. Ricevi una proposta entro 24 ore',
    es: '2. Recibe una propuesta en 24 h',
    de: '2. Erhalten Sie ein Angebot innerhalb von 24 Stunden',
    nl: '2. Ontvang een voorstel binnen 24 uur',
  },
  step2Description: {
    id: `${groupTravelScope}.step2Description`,
    defaultMessage:
      'Notre équipe vous propose les meilleures combinaisons train/bus aux meilleurs tarifs, avec plusieurs options pour comparer.',
    en: 'Our team offers you the best train/bus combinations at the best rates, with several options to compare.',
    it: 'Il nostro team ti propone le migliori combinazioni treno/autobus alle migliori tariffe, con diverse opzioni da confrontare.',
    es: 'Nuestro equipo te propone las mejores combinaciones tren/autobús a las mejores tarifas, con varias opciones para comparar.',
    de: 'Unser Team bietet Ihnen die besten Zug-/Buskombinationen zu den besten Preisen mit mehreren Optionen zum Vergleichen.',
    nl: 'Ons team biedt u de beste trein-/buscombinaties tegen de beste tarieven, met meerdere opties om te vergelijken.',
  },
  step3Title: {
    id: `${groupTravelScope}.step3Title`,
    defaultMessage: '3. Confirmez & voyagez',
    en: '3. Confirm & travel',
    it: '3. Conferma e viaggia',
    es: '3. Confirma y viaja',
    de: '3. Bestätigen & reisen',
    nl: '3. Bevestig & reis',
  },
  step3Description: {
    id: `${groupTravelScope}.step3Description`,
    defaultMessage:
      'Validez votre devis, recevez vos billets centralisés et profitez du voyage. Notre équipe reste disponible pour toute modification.',
    en: 'Confirm your quote, receive your centralised tickets and enjoy the trip. Our team remains available for any change.',
    it: 'Conferma il tuo preventivo, ricevi i tuoi biglietti centralizzati e goditi il viaggio. Il nostro team rimane disponibile per ogni modifica.',
    es: 'Valida tu presupuesto, recibe tus billetes centralizados y disfruta del viaje. Nuestro equipo sigue disponible para cualquier modificación.',
    de: 'Bestätigen Sie Ihr Angebot, erhalten Sie Ihre zentralisierten Tickets und genießen Sie die Reise. Unser Team steht für jede Änderung zur Verfügung.',
    nl: 'Bevestig uw offerte, ontvang uw gecentraliseerde tickets en geniet van de reis. Ons team blijft beschikbaar voor elke wijziging.',
  },

  // FAQ
  faqTitle: {
    id: `${groupTravelScope}.faqTitle`,
    defaultMessage: 'Questions fréquentes',
    en: 'Frequently asked questions',
    it: 'Domande frequenti',
    es: 'Preguntas frecuentes',
    de: 'Häufig gestellte Fragen',
    nl: 'Veelgestelde vragen',
  },
  faq1Question: {
    id: `${groupTravelScope}.faq1Question`,
    defaultMessage:
      'À partir de combien de personnes parle-t-on d’un voyage en groupe ?',
    en: 'From how many people is it considered group travel?',
    it: 'Da quante persone si parla di viaggio di gruppo?',
    es: '¿A partir de cuántas personas se considera viaje en grupo?',
    de: 'Ab wie vielen Personen spricht man von einer Gruppenreise?',
    nl: 'Vanaf hoeveel personen spreekt men van een groepsreis?',
  },
  faq1Answer: {
    id: `${groupTravelScope}.faq1Answer`,
    defaultMessage:
      'Un voyage en groupe commence à partir de 10 personnes voyageant ensemble sur le même trajet et aux mêmes dates. Tictactrip prend en charge des groupes de 10 à plusieurs centaines de personnes.',
    en: 'Group travel starts from 10 people travelling together on the same route and dates. Tictactrip handles groups from 10 to several hundred people.',
    it: 'Un viaggio di gruppo inizia a partire da 10 persone che viaggiano insieme sullo stesso itinerario e nelle stesse date. Tictactrip gestisce gruppi da 10 a diverse centinaia di persone.',
    es: 'Un viaje en grupo empieza a partir de 10 personas que viajan juntas en el mismo trayecto y en las mismas fechas. Tictactrip se encarga de grupos de 10 a varios cientos de personas.',
    de: 'Eine Gruppenreise beginnt ab 10 Personen, die gemeinsam auf derselben Strecke und an denselben Tagen reisen. Tictactrip betreut Gruppen von 10 bis zu mehreren Hundert Personen.',
    nl: 'Een groepsreis begint vanaf 10 personen die samen op dezelfde route en dezelfde data reizen. Tictactrip verzorgt groepen van 10 tot enkele honderden personen.',
  },
  faq2Question: {
    id: `${groupTravelScope}.faq2Question`,
    defaultMessage: 'Quels transports propose Tictactrip pour les groupes ?',
    en: 'Which means of transport does Tictactrip offer for groups?',
    it: 'Quali mezzi di trasporto offre Tictactrip per i gruppi?',
    es: '¿Qué transportes ofrece Tictactrip para los grupos?',
    de: 'Welche Verkehrsmittel bietet Tictactrip für Gruppen an?',
    nl: 'Welke vervoermiddelen biedt Tictactrip aan voor groepen?',
  },
  faq2Answer: {
    id: `${groupTravelScope}.faq2Answer`,
    defaultMessage:
      'Tictactrip propose des trajets en train (SNCF, Ouigo, TGV INOUI, Intercités, TER, Thalys, Eurostar) et en bus (Flixbus, Blablabus, Ouibus, Altibus) partout en France et en Europe. Nous combinons les modes pour optimiser le prix et la durée.',
    en: 'Tictactrip offers trips by train (SNCF, Ouigo, TGV INOUI, Intercités, TER, Thalys, Eurostar) and bus (Flixbus, Blablabus, Ouibus, Altibus) across France and Europe. We combine modes to optimise price and duration.',
    it: 'Tictactrip offre viaggi in treno (SNCF, Ouigo, TGV INOUI, Intercités, TER, Thalys, Eurostar) e in autobus (Flixbus, Blablabus, Ouibus, Altibus) in tutta la Francia e in Europa. Combiniamo i modi per ottimizzare prezzo e durata.',
    es: 'Tictactrip ofrece viajes en tren (SNCF, Ouigo, TGV INOUI, Intercités, TER, Thalys, Eurostar) y en autobús (Flixbus, Blablabus, Ouibus, Altibus) por toda Francia y Europa. Combinamos modos para optimizar precio y duración.',
    de: 'Tictactrip bietet Reisen mit dem Zug (SNCF, Ouigo, TGV INOUI, Intercités, TER, Thalys, Eurostar) und dem Bus (Flixbus, Blablabus, Ouibus, Altibus) in ganz Frankreich und Europa. Wir kombinieren die Verkehrsmittel, um Preis und Dauer zu optimieren.',
    nl: 'Tictactrip biedt reizen per trein (SNCF, Ouigo, TGV INOUI, Intercités, TER, Thalys, Eurostar) en bus (Flixbus, Blablabus, Ouibus, Altibus) in heel Frankrijk en Europa. We combineren modi om prijs en duur te optimaliseren.',
  },
  faq3Question: {
    id: `${groupTravelScope}.faq3Question`,
    defaultMessage: 'En combien de temps recevons-nous le devis ?',
    en: 'How long does it take to receive the quote?',
    it: 'In quanto tempo riceviamo il preventivo?',
    es: '¿En cuánto tiempo recibimos el presupuesto?',
    de: 'In welcher Zeit erhalten wir das Angebot?',
    nl: 'Hoe snel ontvangen wij de offerte?',
  },
  faq3Answer: {
    id: `${groupTravelScope}.faq3Answer`,
    defaultMessage:
      'Notre équipe vous répond sous 24 heures ouvrées avec une proposition détaillée et personnalisée. Pour les demandes urgentes, contactez-nous directement à {email} en précisant l’urgence.',
    en: 'Our team gets back to you within 24 business hours with a detailed and personalised proposal. For urgent requests, contact us directly at {email} and specify the urgency.',
    it: 'Il nostro team ti risponde entro 24 ore lavorative con una proposta dettagliata e personalizzata. Per richieste urgenti, contattaci direttamente a {email} specificando l’urgenza.',
    es: 'Nuestro equipo te responde en 24 horas laborables con una propuesta detallada y personalizada. Para solicitudes urgentes, contáctanos directamente a {email} indicando la urgencia.',
    de: 'Unser Team meldet sich innerhalb von 24 Werkstunden mit einem detaillierten und personalisierten Angebot bei Ihnen. Für dringende Anfragen kontaktieren Sie uns direkt unter {email} und geben Sie die Dringlichkeit an.',
    nl: 'Ons team reageert binnen 24 werkuren met een gedetailleerd en gepersonaliseerd voorstel. Voor spoedaanvragen kunt u direct contact opnemen met {email} en de urgentie vermelden.',
  },
  faq4Question: {
    id: `${groupTravelScope}.faq4Question`,
    defaultMessage: 'Quels sont les modes de paiement acceptés ?',
    en: 'Which payment methods are accepted?',
    it: 'Quali metodi di pagamento sono accettati?',
    es: '¿Qué métodos de pago se aceptan?',
    de: 'Welche Zahlungsmethoden werden akzeptiert?',
    nl: 'Welke betaalmethoden worden geaccepteerd?',
  },
  faq4Answer: {
    id: `${groupTravelScope}.faq4Answer`,
    defaultMessage:
      'Carte bancaire, virement, mandat administratif (pour les écoles, collectivités et administrations) ou paiement échelonné selon le projet. Facture centralisée délivrée systématiquement.',
    en: 'Credit card, bank transfer, administrative order (for schools, local authorities and administrations) or instalment payment depending on the project. A centralised invoice is always issued.',
    it: 'Carta di credito, bonifico, mandato amministrativo (per scuole, enti locali e amministrazioni) o pagamento rateale a seconda del progetto. Fattura centralizzata sempre emessa.',
    es: 'Tarjeta bancaria, transferencia, mandato administrativo (para escuelas, administraciones y entes locales) o pago a plazos según el proyecto. Factura centralizada emitida sistemáticamente.',
    de: 'Kreditkarte, Überweisung, Verwaltungsanordnung (für Schulen, Gebietskörperschaften und Behörden) oder Ratenzahlung je nach Projekt. Eine zentralisierte Rechnung wird stets ausgestellt.',
    nl: 'Creditcard, overschrijving, administratieve opdracht (voor scholen, lokale overheden en administraties) of gespreide betaling afhankelijk van het project. Een gecentraliseerde factuur wordt systematisch verstrekt.',
  },
  faq5Question: {
    id: `${groupTravelScope}.faq5Question`,
    defaultMessage: 'Peut-on modifier ou annuler la réservation ?',
    en: 'Can we change or cancel the booking?',
    it: 'È possibile modificare o annullare la prenotazione?',
    es: '¿Se puede modificar o anular la reserva?',
    de: 'Kann die Buchung geändert oder storniert werden?',
    nl: 'Kan de boeking gewijzigd of geannuleerd worden?',
  },
  faq5Answer: {
    id: `${groupTravelScope}.faq5Answer`,
    defaultMessage:
      'Oui. Les conditions varient selon le transporteur, mais la plupart de nos offres groupe permettent une modification jusqu’à 48 h avant le départ et une annulation partielle avec pénalités réduites par rapport au tarif individuel.',
    en: 'Yes. Conditions vary by carrier, but most of our group offers allow changes up to 48 hours before departure and partial cancellation with reduced penalties compared with individual fares.',
    it: 'Sì. Le condizioni variano in base al vettore, ma la maggior parte delle nostre offerte di gruppo consentono modifiche fino a 48 ore prima della partenza e cancellazione parziale con penali ridotte rispetto alla tariffa individuale.',
    es: 'Sí. Las condiciones varían según el transportista, pero la mayoría de nuestras ofertas de grupo permiten modificación hasta 48 h antes de la salida y cancelación parcial con penalizaciones reducidas respecto a la tarifa individual.',
    de: 'Ja. Die Bedingungen variieren je nach Transportunternehmen, aber die meisten unserer Gruppenangebote erlauben Änderungen bis 48 Stunden vor Abfahrt und eine teilweise Stornierung mit reduzierten Gebühren gegenüber dem Einzeltarif.',
    nl: 'Ja. De voorwaarden verschillen per vervoerder, maar de meeste van onze groepsaanbiedingen staan wijzigingen toe tot 48 uur voor vertrek en gedeeltelijke annulering met lagere boetes dan bij individuele tarieven.',
  },
  faq6Question: {
    id: `${groupTravelScope}.faq6Question`,
    defaultMessage: 'Couvrez-vous toute l’Europe ?',
    en: 'Do you cover all of Europe?',
    it: 'Coprite tutta l’Europa?',
    es: '¿Cubrís toda Europa?',
    de: 'Decken Sie ganz Europa ab?',
    nl: 'Bestrijken jullie heel Europa?',
  },
  faq6Answer: {
    id: `${groupTravelScope}.faq6Answer`,
    defaultMessage:
      'Oui. Tictactrip dessert toute la France et la majeure partie de l’Europe : Belgique, Pays-Bas, Allemagne, Italie, Espagne, Suisse, Royaume-Uni et bien plus. Nous gérons les correspondances internationales train + bus.',
    en: 'Yes. Tictactrip serves all of France and most of Europe: Belgium, the Netherlands, Germany, Italy, Spain, Switzerland, the United Kingdom and many more. We manage international train + bus connections.',
    it: 'Sì. Tictactrip serve tutta la Francia e gran parte dell’Europa: Belgio, Paesi Bassi, Germania, Italia, Spagna, Svizzera, Regno Unito e molto altro. Gestiamo le coincidenze internazionali treno + autobus.',
    es: 'Sí. Tictactrip cubre toda Francia y la mayor parte de Europa: Bélgica, Países Bajos, Alemania, Italia, España, Suiza, Reino Unido y mucho más. Gestionamos las conexiones internacionales tren + autobús.',
    de: 'Ja. Tictactrip bedient ganz Frankreich und den größten Teil Europas: Belgien, die Niederlande, Deutschland, Italien, Spanien, die Schweiz, das Vereinigte Königreich und viele andere. Wir verwalten internationale Zug-/Busverbindungen.',
    nl: 'Ja. Tictactrip bedient heel Frankrijk en het grootste deel van Europa: België, Nederland, Duitsland, Italië, Spanje, Zwitserland, het Verenigd Koninkrijk en veel meer. Wij regelen internationale trein-/busaansluitingen.',
  },

  // Contact CTA
  contactTitle: {
    id: `${groupTravelScope}.contactTitle`,
    defaultMessage: 'Prêt à organiser votre voyage en groupe ?',
    en: 'Ready to organise your group trip?',
    it: 'Pronto a organizzare il tuo viaggio di gruppo?',
    es: '¿Listo para organizar tu viaje en grupo?',
    de: 'Bereit, Ihre Gruppenreise zu organisieren?',
    nl: 'Klaar om uw groepsreis te organiseren?',
  },
  contactDescription: {
    id: `${groupTravelScope}.contactDescription`,
    defaultMessage:
      'Envoyez-nous votre demande à {email}. Notre équipe vous répond sous 24 heures avec une proposition personnalisée, gratuite et sans engagement.',
    en: 'Send your request to {email}. Our team will reply within 24 hours with a personalised, free and no-commitment proposal.',
    it: 'Inviaci la tua richiesta a {email}. Il nostro team risponde entro 24 ore con una proposta personalizzata, gratuita e senza impegno.',
    es: 'Envíanos tu solicitud a {email}. Nuestro equipo te responde en 24 horas con una propuesta personalizada, gratuita y sin compromiso.',
    de: 'Senden Sie Ihre Anfrage an {email}. Unser Team antwortet innerhalb von 24 Stunden mit einem persönlichen, kostenlosen und unverbindlichen Angebot.',
    nl: 'Stuur uw aanvraag naar {email}. Ons team reageert binnen 24 uur met een persoonlijk, gratis en vrijblijvend voorstel.',
  },
  contactCta: {
    id: `${groupTravelScope}.contactCta`,
    defaultMessage: 'Écrire à sales@tictactrip.eu',
    en: 'Email sales@tictactrip.eu',
    it: 'Scrivi a sales@tictactrip.eu',
    es: 'Escribir a sales@tictactrip.eu',
    de: 'Schreiben an sales@tictactrip.eu',
    nl: 'Mail naar sales@tictactrip.eu',
  },
  emailSubject: {
    id: `${groupTravelScope}.emailSubject`,
    defaultMessage: 'Demande de devis groupe',
    en: 'Group quote request',
    it: 'Richiesta di preventivo gruppo',
    es: 'Solicitud de presupuesto grupo',
    de: 'Anfrage Gruppenangebot',
    nl: 'Aanvraag groepsofferte',
  },

  // Footer link
  groupTravelLink: {
    id: `${groupTravelScope}.groupTravelLink`,
    defaultMessage: 'Voyager en groupe',
    en: 'Group travel',
    it: 'Viaggi di gruppo',
    es: 'Viajes en grupo',
    de: 'Gruppenreisen',
    nl: 'Groepsreizen',
  },
};

export default defineMessages(groupTravelMessages);
